import { ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { LucideIcon, Phone } from "lucide-react";
import Breadcrumbs from "./Breadcrumbs";

/**
 * Phase 3b hero.
 *
 * Two shapes:
 *  1. Split hero (when `image` is supplied) — 55% text on --ground-dark,
 *     45% photo bleeding to the right viewport edge, min-height 440px.
 *     Mobile stacks: 16:9 photo on top, then the text block.
 *  2. Solid band (no image) — plain charcoal used by hub/utility pages.
 *
 * The legacy `heroConfig` prop is still accepted so existing callers compile;
 * its values are deliberately ignored.
 */
export interface HeroConfig {
  gradient?: string;
  pattern?: string;
  watermarkIcon?: LucideIcon;
  watermarkPosition?: "top-right" | "bottom-left" | "bottom-right" | "center-right";
  textAlign?: "center" | "left";
}

export interface HeroCta {
  label: string;
  href: string;
  /** true → renders a plain <a> (tel:, mailto:, external) instead of a router Link */
  external?: boolean;
  variant?: "primary" | "outline";
  /** show the phone glyph (the only SVG allowed in the hero besides breadcrumb chevrons) */
  phoneIcon?: boolean;
  dataCta?: string;
}

interface PageHeroProps {
  title: string;
  subtitle?: string | undefined;
  eyebrow?: string | undefined;
  /** Lede paragraph shown directly under the H1 in the split hero. */
  lede?: string | undefined;
  /** Optional bolder first line of the lede (the former hero subtitle). */
  ledeLead?: string | undefined;
  ctas?: HeroCta[] | undefined;
  image?: string | undefined;
  imageAlt?: string | undefined;
  breadcrumbs?: { label: string; href?: string }[] | undefined;
  children?: ReactNode;
  centered?: boolean | undefined;
  heroConfig?: HeroConfig | undefined;
}

const ctaClasses = (variant: HeroCta["variant"]) =>
  variant === "outline"
    ? "inline-flex items-center justify-center gap-2 rounded-[4px] border border-white/70 px-4 py-2.5 font-heading text-[14px] font-bold text-white md:px-6 md:py-3 md:text-[15px] transition-colors hover:bg-white/10"
    : "inline-flex items-center justify-center gap-2 rounded-[4px] bg-primary px-4 py-2.5 font-heading text-[14px] font-bold text-primary-foreground md:px-6 md:py-3 md:text-[15px] transition-colors hover:bg-primary/90";

const CtaLink = ({ cta }: { cta: HeroCta }) => {
  const cls = ctaClasses(cta.variant);
  const inner = (
    <>
      {cta.phoneIcon && <Phone className="h-4 w-4" aria-hidden="true" />}
      {cta.label}
    </>
  );
  if (cta.external) {
    return (
      <a href={cta.href} data-cta={cta.dataCta} className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={cta.href} data-cta={cta.dataCta} className={cls}>
      {inner}
    </Link>
  );
};

const PageHero = ({
  title,
  subtitle,
  eyebrow,
  lede,
  ledeLead,
  ctas,
  image,
  imageAlt,
  breadcrumbs,
  children,
  centered: _centered = false,
  heroConfig: _heroConfig,
}: PageHeroProps) => {
  if (image) {
    return (
      <section className="relative isolate overflow-hidden bg-charcoal text-white">
        {/* Photo — stacked 16:9 on mobile, bleeding to the right edge from lg up */}
        <div className="relative aspect-[3/1] w-full lg:absolute lg:inset-y-0 lg:right-0 lg:aspect-auto lg:h-full lg:w-[45%]">
          <img
            src={image}
            alt={imageAlt || ""}
            width="1200"
            height="900"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
            aria-hidden={imageAlt ? undefined : true}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/10 to-transparent" aria-hidden="true" />
        </div>

        <div className="container relative">
          <div className="flex flex-col justify-center py-5 lg:min-h-[440px] lg:w-[55%] lg:py-14 lg:pr-10">
            {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
            {eyebrow && (
              <p className="mt-3 font-condensed text-[13px] font-bold uppercase tracking-[0.16em] text-primary-light">
                {eyebrow}
              </p>
            )}
            <h1 className="mt-2 max-w-[20ch] font-heading text-[1.6rem] font-extrabold leading-[1.08] tracking-[-0.02em] md:text-[2.5rem] lg:text-[2.9rem]">
              {title}
            </h1>
            {ledeLead && (
              <p className="mt-3 max-w-[52ch] font-body text-[18px] font-medium leading-[1.5] text-white/85">
                {ledeLead}
              </p>
            )}
            {lede && (
              <p className="mt-3 max-w-[52ch] font-body text-[13px] leading-[1.55] text-white/80 md:text-base">
                {lede}
              </p>
            )}
            {subtitle && (
              <p className="mt-3 max-w-[52ch] font-body text-[15px] leading-relaxed text-white/80 md:text-base">
                {subtitle}
              </p>
            )}
            {ctas && ctas.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2 md:gap-3">
                {ctas.map((cta) => (
                  <CtaLink key={cta.href + cta.label} cta={cta} />
                ))}
              </div>
            )}
            {children}
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-px bg-white/10" aria-hidden="true" />
      </section>
    );
  }

  return (
    <section className="relative isolate overflow-hidden bg-charcoal text-white">
      <div className="absolute inset-x-0 bottom-0 h-px bg-white/10" aria-hidden="true" />
      <div className="container relative flex min-h-[240px] flex-col justify-end py-8 md:min-h-[320px] md:py-12">
        {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
        {eyebrow && (
          <p className="mt-2 font-condensed text-[13px] font-bold uppercase tracking-[0.14em] text-primary-light">
            {eyebrow}
          </p>
        )}
        <h1 className="mt-2 max-w-[22ch] font-heading text-[1.9rem] font-extrabold leading-[1.08] tracking-[-0.02em] md:text-[2.75rem] lg:text-[3rem]">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-3 max-w-[58ch] font-body text-[15px] leading-relaxed text-white/80 md:text-base">
            {subtitle}
          </p>
        )}
        {ledeLead && (
          <p className="mt-3 max-w-[58ch] font-body text-[18px] font-medium leading-relaxed text-white/85">
            {ledeLead}
          </p>
        )}
        {lede && (
          <p className="mt-3 max-w-[58ch] font-body text-[15px] leading-relaxed text-white/80 md:text-base">
            {lede}
          </p>
        )}
        {ctas && ctas.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-3">
            {ctas.map((cta) => <CtaLink key={cta.href + cta.label} cta={cta} />)}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

export default PageHero;
